/** @format */

import { AemPinnedContentSpotTranslated, PersonalizedPCS } from '@plp/data-access/interfaces';
import { BOPIS_FILTER, FACET_STORE, FILTER_ID_5004, SALE_FILTER } from '@plp/data-access/constants';
import {
  BeaconQueryParameters,
  Chain,
  CriteoPinnedContentSpot,
} from '@shared/data-access/interfaces';
import { BreakpointsService, UtilityService } from '@shared/data-access/services';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';

import { BreakPointNew } from '@shared/data-access/enums';
import { DmnAdService } from '@shared/data-access/product-recommendations';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { MediaComponent } from '@shared/media/components';
import { PzPinnedContentType } from '@plp/data-access/enums';
import { Store } from '@ngrx/store';
import { filteringPinnedContentClicked } from '@plp/data-access/state';

@Component({
  selector: 'plp-components-pinned-content',
  imports: [NgClass, NgTemplateOutlet, HmfButtonComponent, MediaComponent],
  templateUrl: './pinned-content.component.html',
  styleUrl: './pinned-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PinnedContentComponent {
  private readonly chain = inject(Chain);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly breakpointsService = inject(BreakpointsService);

  private readonly beaconQueryParameters: BeaconQueryParameters = {
    placementType: 'DST',
    placementLevel: 'PLACEMENT',
    beaconType: 'CLICK',
  };

  availabilityOnProductCardsEnabled = input(true);
  column = input(0);
  dataEm = input('');
  imagePagePosition = input(0);
  lazyLoad = input(false);
  pinnedContent = input.required<
    AemPinnedContentSpotTranslated | CriteoPinnedContentSpot | PersonalizedPCS
  >();

  row = input(0);
  showCustomerRating = input(false);
  isSponsored = input(false);

  isPublicLands = this.chain.chainIdentifierAbbr === 'pl';
  replace = /[^0-9a-zA-Z]/g;
  altText = computed(() =>
    this.breakpointsService.breakpoint() >= BreakPointNew.MEDIUM
      ? (this.pinnedContent() as AemPinnedContentSpotTranslated)?.altText || ''
      : (this.pinnedContent() as AemPinnedContentSpotTranslated)?.mobileAltText || '',
  );

  image = computed(() => (this.pinnedContent() as AemPinnedContentSpotTranslated)?.image || '');
  mobileImage = computed(
    () => (this.pinnedContent() as AemPinnedContentSpotTranslated)?.mobileImage || '',
  );

  /**
   * @description Determines if the PCS is standard (not personalized BOPIS or DEAL_SEEKERS)
   */
  readonly isStandardPCS = computed(() => {
    const content = this.pinnedContent();
    const pcsType = (content as PersonalizedPCS)?.pcsType;
    return !pcsType || pcsType === PzPinnedContentType.STANDARD;
  });

  /**
   * @description Fires Criteo beacon for sponsored content tracking
   */
  fireBeacon(): void {
    if ((this.pinnedContent() as CriteoPinnedContentSpot)?.onClickBeacon) {
      this.dmnAdService.sendBeacon(
        (this.pinnedContent() as CriteoPinnedContentSpot).onClickBeacon,
        this.beaconQueryParameters,
      );
    }
  }

  /**
   * @description Handles personalized PCS click actions (BOPIS, DEAL_SEEKERS)
   */
  handlePzPinnedContent(): void {
    this.fireBeacon();
    const pcsType = (this.pinnedContent() as PersonalizedPCS)?.pcsType;
    switch (pcsType) {
      case PzPinnedContentType.DEAL_SEEKERS:
        this.pinnedContentApplyFilterAndRefresh({
          [FILTER_ID_5004]: [SALE_FILTER],
        });
        break;
      case PzPinnedContentType.BOPIS:
        this.pinnedContentApplyFilterAndRefresh({ [FACET_STORE]: [BOPIS_FILTER] });
        break;
    }
  }

  /**
   * @description Applies filter and refreshes product list scrolling to top
   */
  private pinnedContentApplyFilterAndRefresh(selectedPCFilter: Record<string, string[]>): void {
    this.store.dispatch(
      filteringPinnedContentClicked({
        selectedFilters: selectedPCFilter,
      }),
    );
    queueMicrotask(() => {
      const plpContainerElem = document.getElementById('plp-list-container');
      if (plpContainerElem) {
        this.utilityService.scrollToElement(plpContainerElem, { behavior: 'instant' });
      }
    });
  }
}
